import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Lenis from "lenis";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ClipboardList,
  Truck,
  HandCoins,
  CalendarCheck,
  PackageCheck,
  ShieldQuestion,
  ArrowRight,
  ArrowDown,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ApplicationForm } from "@/components/ApplicationForm";
import { Concierge } from "@/components/Concierge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG =
  "https://images.unsplash.com/photo-1758448721161-7b3df5ec04b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHx1bmRlcmdyb3VuZCUyMGNhciUyMHBhcmslMjBsdXh1cnl8ZW58MHx8fHwxNzg5MTkzNjI5fDA&ixlib=rb-4.1.0&q=85";
const STEERING_IMG = "/steering-lock.png";

const EASE = [0.16, 1, 0.3, 1];

const scrollToApply = () => {
  if (window.__lenis) {
    window.__lenis.scrollTo("#apply", { offset: -70, duration: 1.6 });
  } else {
    document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });
  }
};

const MaskedLine = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <span ref={ref} className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "115%" }}
        animate={inView ? { y: 0 } : { y: "115%" }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
};

const Reveal = ({ children, delay = 0, className }) => (
  <motion.div
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

const ChapterHeading = ({ num, eyebrow, title, dark = false }) => (
  <div className="relative">
    <span
      aria-hidden
      className="pointer-events-none absolute -top-20 -left-3 select-none font-serif text-[9rem] md:text-[12rem] leading-none text-transparent"
      style={{
        WebkitTextStroke: dark
          ? "1px rgba(255,255,255,0.14)"
          : "1px rgba(15,23,42,0.12)",
      }}
    >
      {num}
    </span>
    <MaskedLine>
      <span
        className={`text-xs font-bold uppercase tracking-[0.3em] ${
          dark ? "text-blue-300" : "text-accent"
        }`}
      >
        {eyebrow}
      </span>
    </MaskedLine>
    <h2
      className={`mt-4 font-serif text-4xl md:text-6xl tracking-tight leading-[1.05] ${
        dark ? "text-white" : "text-primary"
      }`}
    >
      {title}
    </h2>
  </div>
);

const PilotCounter = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/applications/count`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const claimed = Math.min(data.count, data.goal);
  return (
    <div data-testid="pilot-spots-counter" className="mt-8 flex items-center gap-3">
      <div className="h-1 w-44 overflow-hidden rounded-full bg-white/15">
        <div
          className="h-full rounded-full bg-blue-400 transition-[width] duration-700"
          style={{ width: `${(claimed / data.goal) * 100}%` }}
        />
      </div>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200">
        {claimed} of {data.goal} pilot places claimed
      </p>
    </div>
  );
};

const Nav = () => (
  <motion.header
    data-testid="site-nav"
    initial={{ y: -80 }}
    animate={{ y: 0 }}
    transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
    className="fixed top-0 z-50 w-full border-b border-white/10 bg-slate-950/70 backdrop-blur-xl"
  >
    <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
      <a href="/" data-testid="nav-logo" className="group flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-slate-950 transition-transform duration-500 group-hover:-rotate-12">
          <Lock className="h-4 w-4" />
        </span>
        <span className="font-serif text-lg font-semibold tracking-tight text-white">
          SecureStay <span className="italic text-blue-300">Locks</span>
        </span>
      </a>
      <div className="flex items-center gap-6">
        <span className="hidden text-[11px] font-bold uppercase tracking-[0.25em] text-slate-400 md:block">
          London · 2026
        </span>
        <Button
          data-testid="nav-apply-btn"
          onClick={scrollToApply}
          className="group rounded-full bg-accent px-6 font-semibold text-accent-foreground hover:bg-blue-500 transition-colors"
        >
          Apply for free pilot
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Button>
      </div>
    </div>
  </motion.header>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.25]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const rise = useTransform(scrollYProgress, [0, 1], [0, -120]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden bg-slate-950"
    >
      <motion.img
        src={HERO_IMG}
        alt="Hotel car park at night"
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 h-full w-full object-cover will-change-transform"
      />
      <div className="absolute inset-0 bg-slate-950/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/40" />

      <motion.div
        style={{ opacity: fade, y: rise }}
        className="relative mx-auto w-full max-w-7xl px-6 pb-32 pt-44"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
          className="mb-8 inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/5 px-5 py-2 text-[11px] font-bold uppercase tracking-[0.3em] text-blue-200 backdrop-blur-sm"
        >
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-400" />
          Free pilot — London hotels
        </motion.p>

        <h1 className="max-w-5xl font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] tracking-tight text-white leading-[1.02]">
          <MaskedLine delay={0.55}>Give your guests</MaskedLine>
          <MaskedLine delay={0.72}>
            <span className="italic text-blue-300">free peace of mind</span>
          </MaskedLine>
          <MaskedLine delay={0.89}>for their car.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-slate-300"
        >
          SecureStay Locks lends your guests a high-visibility steering wheel
          lock — a thoughtful, free amenity that adds a visible deterrent in
          your car park and sets your hotel apart.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
          className="mt-12 flex flex-wrap items-center gap-6"
        >
          <Button
            data-testid="hero-apply-btn"
            onClick={scrollToApply}
            size="lg"
            className="group h-14 rounded-full bg-accent px-9 text-base font-semibold text-accent-foreground shadow-[0_0_40px_rgba(37,99,235,0.45)] hover:bg-blue-500 transition-colors"
          >
            Apply for free pilot
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Button>
          <p className="text-sm text-slate-400">
            1 month free · No upfront cost · Min. 10 locks
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.55 }}
        >
          <PilotCounter />
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9, duration: 1 }}
        className="absolute bottom-0 left-0 right-0 border-t border-white/10"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 text-[11px] font-bold uppercase tracking-[0.25em] text-slate-400">
          <span>A guest amenity, not a gadget</span>
          <span className="hidden md:block">Deterring drive-off & relay theft</span>
          <span className="flex items-center gap-2">
            Scroll <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
          </span>
        </div>
      </motion.div>
    </section>
  );
};

const MARQUEE_ITEMS = [
  "Peace of mind for every guest car",
  "A visible deterrent",
  "Free with every stay",
  "Your hotel could be first",
];

const EditorialMarquee = () => (
  <section
    data-testid="social-proof-section"
    className="overflow-hidden border-y border-border bg-white py-10 md:py-14"
  >
    <div className="flex w-max animate-marquee items-center gap-16 pr-16">
      {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
        <div
          key={i}
          data-testid={`partner-placeholder-${i}`}
          className="flex items-center gap-16 whitespace-nowrap"
        >
          <span className="font-serif text-3xl md:text-5xl italic tracking-tight text-slate-300">
            {item}
          </span>
          <Lock className="h-5 w-5 text-blue-200" />
        </div>
      ))}
    </div>
  </section>
);

const STEPS = [
  {
    icon: ClipboardList,
    title: "Apply in two minutes",
    text: "Tell us about your car park. If your hotel is a fit, we confirm your pilot dates within two working days.",
  },
  {
    icon: Truck,
    title: "Locks delivered to your door",
    text: "We ship a minimum of 10 locks with a front-desk lending log and guest signage. Nothing to install.",
  },
  {
    icon: HandCoins,
    title: "Guests borrow at check-in",
    text: "Your desk takes a small refundable holding deposit, hands over a lock, and logs the return at checkout.",
  },
];

const HowItWorks = () => (
  <section data-testid="how-it-works-section" className="relative py-24 md:py-36">
    <div className="mx-auto max-w-7xl px-6">
      <ChapterHeading
        num="01"
        eyebrow="The amenity"
        title={
          <>
            Three steps.
            <br />
            <span className="italic text-accent">Zero software.</span>
          </>
        }
      />
      <div className="mt-16 md:mt-24">
        {STEPS.map((step, i) => (
          <Reveal key={step.title} delay={i * 0.08}>
            <div
              data-testid={`how-it-works-step-${i + 1}`}
              className="group grid grid-cols-1 items-start gap-6 border-t border-border py-10 transition-colors duration-500 hover:bg-white md:grid-cols-[120px_1fr_auto] md:items-center md:py-12"
            >
              <span className="font-serif text-6xl md:text-7xl leading-none text-slate-200 transition-colors duration-500 group-hover:text-accent">
                0{i + 1}
              </span>
              <div className="max-w-xl">
                <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-primary">
                  {step.title}
                </h3>
                <p className="mt-3 text-sm md:text-base leading-relaxed text-muted-foreground">
                  {step.text}
                </p>
              </div>
              <span className="hidden h-14 w-14 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors duration-500 group-hover:border-accent group-hover:bg-accent group-hover:text-white md:flex">
                <step.icon className="h-5 w-5" />
              </span>
            </div>
          </Reveal>
        ))}
        <div className="border-t border-border" />
      </div>
    </div>
  </section>
);

const OFFER_POINTS = [
  { icon: CalendarCheck, text: "1-month free pilot — no upfront cost, ever, during the trial" },
  { icon: PackageCheck, text: "Minimum 10 locks supplied, delivered and collected by us" },
  { icon: ClipboardList, text: "Fixed start and end dates agreed in writing before we ship" },
  { icon: ShieldQuestion, text: "No obligation to continue — hand the locks back at the end" },
];

const PilotOffer = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const frameY = useTransform(scrollYProgress, [0, 1], [-24, 24]);

  return (
    <section
      ref={ref}
      data-testid="pilot-offer-section"
      className="relative overflow-hidden bg-white py-24 md:py-36"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-2">
        <div className="relative order-2 lg:order-1">
          <motion.div
            style={{ y: frameY }}
            className="absolute inset-0 translate-x-5 translate-y-5 rounded-lg border-2 border-accent/25"
          />
          <motion.div style={{ y: imgY }} className="relative will-change-transform">
            <div className="overflow-hidden rounded-lg">
              <motion.img
                src={STEERING_IMG}
                alt="High-visibility steering wheel lock fitted on a guest's car"
                whileHover={{ scale: 1.04 }}
                transition={{ duration: 0.8, ease: EASE }}
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
            <p className="absolute bottom-5 left-5 rounded-full bg-slate-950/80 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white backdrop-blur-sm">
              The amenity, up close
            </p>
          </motion.div>
        </div>

        <div className="order-1 lg:order-2">
          <ChapterHeading
            num="02"
            eyebrow="The pilot offer"
            title={
              <>
                One month.
                <br />
                <span className="italic text-accent">On us.</span>
              </>
            }
          />
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-lg text-base md:text-lg leading-relaxed text-muted-foreground">
              We are validating this amenity with a small group of London
              hotels. Pilots run on clear, written terms — so you always know
              exactly where you stand.
            </p>
          </Reveal>
          <ul className="mt-10 space-y-6">
            {OFFER_POINTS.map((point, i) => (
              <Reveal key={i} delay={0.1 + i * 0.08}>
                <li data-testid={`offer-point-${i}`} className="group flex items-start gap-5">
                  <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border text-accent transition-colors duration-500 group-hover:bg-accent group-hover:text-white">
                    <point.icon className="h-5 w-5" />
                  </span>
                  <span className="text-sm md:text-base leading-relaxed text-foreground">
                    {point.text}
                  </span>
                </li>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={0.4}>
            <Button
              data-testid="offer-apply-btn"
              onClick={scrollToApply}
              size="lg"
              className="group mt-12 h-14 rounded-full bg-primary px-8 font-semibold text-primary-foreground hover:bg-slate-800 transition-colors"
            >
              Apply for free pilot
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const FAQS = [
  {
    q: "Does this expose the hotel to any liability?",
    a: "No. The locks are provided as an as-is guest amenity — like lending an umbrella or a bike. The amenity is provided as-is and your hotel bears no added liability. We put this in writing in the pilot agreement.",
  },
  {
    q: "What happens if a guest loses or doesn't return a lock?",
    a: "The refundable holding deposit covers replacement, and during the pilot we replace lost stock at no cost to you. Unreturned locks are our problem, not yours.",
  },
  {
    q: "How does the guest holding deposit work?",
    a: "Your front desk takes a small refundable deposit — a card pre-auth or cash — when handing over a lock, and returns it when the lock comes back. During the pilot this is handled manually at the desk. No software, no integration.",
  },
  {
    q: "Will a lock stop a determined thief?",
    a: "Honestly, no single device will. A visible steering wheel lock deters opportunistic drive-off and relay theft, but it won't stop a tow truck. We position it as an added deterrent — and that honesty is exactly what builds trust with your guests.",
  },
];

const Faq = () => (
  <section data-testid="faq-section" className="relative py-24 md:py-36">
    <div className="mx-auto max-w-4xl px-6">
      <ChapterHeading
        num="03"
        eyebrow="Straight answers"
        title={
          <>
            The questions
            <br />
            <span className="italic text-accent">every GM asks.</span>
          </>
        }
      />
      <Reveal delay={0.15}>
        <Accordion
          type="single"
          collapsible
          className="mt-14 md:mt-20"
          data-testid="faq-accordion"
        >
          {FAQS.map((faq, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="border-border"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="py-7 font-serif text-xl md:text-2xl tracking-tight text-primary transition-colors hover:text-accent hover:no-underline"
              >
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="max-w-2xl text-sm md:text-base leading-relaxed text-muted-foreground">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

const APPLY_POINTS = [
  "Two-minute application, no card details",
  "Personal reply from the founder, not a mailing list",
  "Fixed pilot dates agreed before anything ships",
];

const ApplySection = () => (
  <section
    id="apply"
    data-testid="apply-section"
    className="relative overflow-hidden bg-slate-950 py-24 md:py-36"
  >
    <div className="pointer-events-none absolute -right-40 -top-40 h-[30rem] w-[30rem] rounded-full bg-accent/20 blur-[120px]" />
    <div className="pointer-events-none absolute -bottom-40 -left-40 h-[30rem] w-[30rem] rounded-full bg-blue-400/10 blur-[120px]" />
    <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-start gap-16 px-6 lg:grid-cols-2">
      <div>
        <ChapterHeading
          dark
          num="04"
          eyebrow="Apply for the free pilot"
          title={
            <>
              Put your hotel
              <br />
              <span className="italic text-blue-300">on the pilot list.</span>
            </>
          }
        />
        <Reveal delay={0.15}>
          <p className="mt-6 max-w-md text-base md:text-lg leading-relaxed text-slate-400">
            Tell us about your car park and we will confirm within two working
            days whether your hotel is a fit. Places are limited while we
            prove the model.
          </p>
        </Reveal>
        <ul className="mt-10 space-y-4">
          {APPLY_POINTS.map((item, i) => (
            <Reveal key={item} delay={0.2 + i * 0.08}>
              <li className="flex items-center gap-4 text-sm md:text-base text-slate-300">
                <span className="h-px w-8 bg-blue-400" />
                {item}
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
      <Reveal delay={0.2}>
        <ApplicationForm />
      </Reveal>
    </div>
  </section>
);

const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-border bg-white py-16 md:py-20">
    <div className="mx-auto max-w-7xl px-6">
      <div className="flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-serif text-4xl md:text-6xl tracking-tight text-primary">
            SecureStay <span className="italic text-accent">Locks</span>
          </p>
          <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.25em] text-muted-foreground">
            A guest amenity pilot — London, 2026
          </p>
        </div>
        <p className="max-w-md text-xs leading-relaxed text-muted-foreground">
          Locks are a visible deterrent, provided as-is; hotels bear no added
          liability. Honest by design: a lock deters drive-off and relay
          theft, not towing. © 2026 SecureStay Locks.
        </p>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let id;
    const raf = (time) => {
      lenis.raf(time);
      id = requestAnimationFrame(raf);
    };
    id = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div data-testid="landing-page">
      <Nav />
      <Hero />
      <EditorialMarquee />
      <HowItWorks />
      <PilotOffer />
      <Faq />
      <ApplySection />
      <Footer />
      <Concierge />
    </div>
  );
}
